/**
 * PricingSettings Component
 *
 * US-014: Pricing Tiers Management
 * Allows administrators to view and edit pricing tier multipliers.
 */

import { useState, useEffect } from 'react'
import { fetchPriceTiers, updatePriceTier, fetchCustomerTypes, updateCustomerType } from '../supabase-client'
import { invalidateCustomerTypesCache } from '../utils/customerTypeMapper'
import { useDataCache } from '../contexts/DataCacheContext'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Pencil, Save, X, AlertCircle, Info, ShoppingCart, Store, Warehouse, Coins } from 'lucide-react'

export default function PricingSettings() {
  const { invalidateCache } = useDataCache()
  const [priceTiers, setPriceTiers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [editingTier, setEditingTier] = useState(null)
  const [editForm, setEditForm] = useState({
    multiplier: '',
    display_name: '',
    description: ''
  })
  const [saving, setSaving] = useState(false)
  const [customerTypes, setCustomerTypes] = useState([])
  const [savingTypeId, setSavingTypeId] = useState(null)

  useEffect(() => {
    loadPriceTiers()
    loadCustomerTypes()
  }, [])

  const loadCustomerTypes = async () => {
    try {
      const types = await fetchCustomerTypes(true)
      setCustomerTypes(types)
    } catch (err) {
      console.error('Failed to load customer types:', err)
    }
  }

  const handleTierChange = async (customerTypeId, newTierId) => {
    try {
      setSavingTypeId(customerTypeId)
      await updateCustomerType(customerTypeId, { default_price_tier_id: newTierId })
      invalidateCustomerTypesCache()
      invalidateCache('priceTiers')
      invalidateCache('productPrices')
      await loadCustomerTypes()
      setSuccess('Customer type pricing tier updated')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Failed to update customer type tier:', err)
      setError('Failed to update tier: ' + err.message)
    } finally {
      setSavingTypeId(null)
    }
  }

  const loadPriceTiers = async () => {
    try {
      setLoading(true)
      setError(null)
      const tiers = await fetchPriceTiers()
      setPriceTiers(tiers)
    } catch (err) {
      console.error('Failed to load price tiers:', err)
      setError('Failed to load pricing tiers. Please refresh the page.')
    } finally {
      setLoading(false)
    }
  }

  const startEditing = (tier) => {
    setEditingTier(tier.id)
    setEditForm({
      multiplier: tier.multiplier.toString(),
      display_name: tier.display_name,
      description: tier.description || ''
    })
    setError(null)
    setSuccess(null)
  }

  const cancelEditing = () => {
    setEditingTier(null)
    setEditForm({ multiplier: '', display_name: '', description: '' })
  }

  const handleSave = async (tierId) => {
    try {
      setSaving(true)
      setError(null)

      const multiplier = parseFloat(editForm.multiplier)
      if (isNaN(multiplier) || multiplier < 0.01 || multiplier > 2.0) {
        setError('Multiplier must be a number between 0.01 and 2.0')
        return
      }

      await updatePriceTier(tierId, {
        multiplier,
        display_name: editForm.display_name,
        description: editForm.description
      })

      // Refresh tiers
      await loadPriceTiers()
      invalidateCache('priceTiers')
      invalidateCache('productPrices')
      setEditingTier(null)
      setSuccess('Pricing tier updated successfully')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Failed to update price tier:', err)
      setError('Failed to update pricing tier: ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  const multiplierToDiscount = (multiplier) => {
    const discount = (1 - multiplier) * 100
    if (discount === 0) return 'No discount'
    return `${discount.toFixed(0)}% discount`
  }

  const getTierIcon = (name) => {
    const iconProps = { className: 'h-6 w-6' }
    switch (name) {
      case 'retail':
        return <ShoppingCart {...iconProps} />
      case 'commercial':
        return <Store {...iconProps} />
      case 'wholesale':
        return <Warehouse {...iconProps} />
      default:
        return <Coins {...iconProps} />
    }
  }

  const getTierColor = (name) => {
    switch (name) {
      case 'retail':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'commercial':
        return 'bg-amber-100 text-amber-800 border-amber-200'
      case 'wholesale':
        return 'bg-green-100 text-green-800 border-green-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getTierIconColor = (name) => {
    switch (name) {
      case 'retail':
        return 'text-blue-600'
      case 'commercial':
        return 'text-amber-600'
      case 'wholesale':
        return 'text-green-600'
      default:
        return 'text-gray-600'
    }
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Pricing Tiers</CardTitle>
          <CardDescription>Loading pricing configuration...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Pricing Tiers</CardTitle>
          <CardDescription>
            Configure pricing multipliers for different customer types. Multipliers are applied to the
            grade-adjusted base price for each product.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert>
              <AlertDescription>{success}</AlertDescription>
            </Alert>
          )}

          {/* Pricing Formula Explanation */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-slate-500 mt-0.5" />
              <div className="text-sm text-slate-600">
                <p className="font-medium mb-1">Pricing Formula:</p>
                <code className="bg-white px-2 py-1 rounded text-xs">
                  Final Price = Base Price x Grade Multiplier x Tier Multiplier
                </code>
                <p className="mt-2">
                  Example: £10.00 base x 1.0 grade x 0.85 commercial = <strong>£8.50</strong>
                </p>
              </div>
            </div>
          </div>

          {/* Price Tiers Grid */}
          <div className="grid gap-4">
            {priceTiers.map((tier) => (
              <div
                key={tier.id}
                className={`border rounded-lg p-4 ${
                  editingTier === tier.id ? 'ring-2 ring-primary border-primary' : ''
                }`}
              >
                {editingTier === tier.id ? (
                  /* Edit Mode */
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={getTierIconColor(tier.name)}>{getTierIcon(tier.name)}</span>
                        <Badge variant="outline" className={getTierColor(tier.name)}>
                          {tier.name}
                        </Badge>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={cancelEditing}
                          disabled={saving}
                        >
                          <X className="h-4 w-4 mr-1" />
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleSave(tier.id)}
                          disabled={saving}
                        >
                          <Save className="h-4 w-4 mr-1" />
                          {saving ? 'Saving...' : 'Save'}
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor={`multiplier-${tier.id}`}>Multiplier</Label>
                        <Input
                          id={`multiplier-${tier.id}`}
                          type="number"
                          step="0.01"
                          min="0.01"
                          max="2.0"
                          value={editForm.multiplier}
                          onChange={(e) => setEditForm({ ...editForm, multiplier: e.target.value })}
                          className="max-w-32"
                        />
                        <p className="text-xs text-muted-foreground">
                          {editForm.multiplier && !isNaN(parseFloat(editForm.multiplier))
                            ? multiplierToDiscount(parseFloat(editForm.multiplier))
                            : 'Enter a value'}
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor={`name-${tier.id}`}>Display Name</Label>
                        <Input
                          id={`name-${tier.id}`}
                          type="text"
                          value={editForm.display_name}
                          onChange={(e) => setEditForm({ ...editForm, display_name: e.target.value })}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor={`desc-${tier.id}`}>Description</Label>
                        <Input
                          id={`desc-${tier.id}`}
                          type="text"
                          value={editForm.description}
                          onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  /* View Mode */
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <span className={getTierIconColor(tier.name)}>{getTierIcon(tier.name)}</span>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold">{tier.display_name}</h3>
                          <Badge variant="outline" className={getTierColor(tier.name)}>
                            {tier.name}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{tier.description}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-2xl font-bold">{tier.multiplier}x</p>
                        <p className="text-sm text-muted-foreground">
                          {multiplierToDiscount(tier.multiplier)}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => startEditing(tier)}
                      >
                        <Pencil className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {priceTiers.length === 0 && !loading && (
            <div className="text-center py-8 text-muted-foreground">
              <p>No pricing tiers found.</p>
              <p className="text-sm">Please run the database migration to create default tiers.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Customer Type → Price Tier Mapping */}
      <Card>
        <CardHeader>
          <CardTitle>Customer Type Mapping</CardTitle>
          <CardDescription>
            Assign which pricing tier applies to each customer type.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {customerTypes.map((ct) => {
              const assignedTier = priceTiers.find(t => t.id === ct.default_price_tier_id)
              return (
                <div key={ct.id} className="flex items-center justify-between border rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <span className="text-muted-foreground text-lg">
                      {ct.icon_name === 'ShoppingCart' && <ShoppingCart className="h-5 w-5" />}
                      {ct.icon_name === 'Store' && <Store className="h-5 w-5" />}
                      {ct.icon_name === 'Warehouse' && <Warehouse className="h-5 w-5" />}
                      {ct.icon_name === 'User' && <Coins className="h-5 w-5" />}
                      {!['ShoppingCart', 'Store', 'Warehouse', 'User'].includes(ct.icon_name) && <Coins className="h-5 w-5" />}
                    </span>
                    <span className="font-medium">{ct.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {savingTypeId === ct.id && (
                      <span className="text-sm text-muted-foreground">Saving...</span>
                    )}
                    <Select
                      value={ct.default_price_tier_id || ''}
                      onValueChange={(value) => handleTierChange(ct.id, value)}
                      disabled={savingTypeId === ct.id}
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="Select tier">
                          {assignedTier && (
                            <span className="flex items-center gap-2">
                              <Badge variant="outline" className={`text-xs ${getTierColor(assignedTier.name)}`}>
                                {assignedTier.display_name}
                              </Badge>
                            </span>
                          )}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {priceTiers.map((tier) => (
                          <SelectItem key={tier.id} value={tier.id}>
                            <span className="flex items-center gap-2">
                              {tier.display_name}
                              <span className="text-muted-foreground text-xs">({tier.multiplier}x)</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )
            })}
            {customerTypes.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">No customer types found.</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
